import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { Sparkles, X, ArrowUpRight, Loader2, Zap } from "lucide-react";
import { toast } from "sonner";

export default function AskCoachButton() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [move, setMove] = useState(null);
  const [doing, setDoing] = useState(false);

  const open_ = async () => {
    setOpen(true);
    setLoading(true);
    try {
      const r = await api.get("/next-best-move");
      setMove(r.data);
    } finally { setLoading(false); }
  };

  const doIt = async () => {
    setDoing(true);
    try {
      const r = await api.post("/next-best-move/execute");
      toast.success(r.data.message);
      setOpen(false);
    } finally { setDoing(false); }
  };

  return (
    <>
      <button data-testid="ask-coach-fab" onClick={open_}
        className="fixed bottom-6 right-6 z-40 gold-btn h-14 pl-5 pr-6 rounded-full inline-flex items-center gap-2 shadow-2xl pulse-glow">
        <Zap className="w-4 h-4" />
        <span className="text-sm font-semibold">One action right now</span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-xl flex items-center justify-center p-4" onClick={() => setOpen(false)}>
            <motion.div initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.85, opacity: 0 }}
              data-testid="focus-card"
              className="glass-card p-10 max-w-2xl w-full border-[rgba(212,175,55,0.4)] relative" onClick={e => e.stopPropagation()}>
              <button onClick={() => setOpen(false)} className="absolute top-4 right-4 text-[color:var(--text-muted)] hover:text-white"><X className="w-5 h-5" /></button>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[rgba(212,175,55,0.12)] text-[color:var(--gold)] text-xs uppercase tracking-widest mb-6">
                <Sparkles className="w-3 h-3" /> Your one focus right now
              </div>
              {loading || !move ? (
                <div className="py-12 text-center"><Loader2 className="w-10 h-10 animate-spin text-[color:var(--gold)] mx-auto" /></div>
              ) : (
                <>
                  <h2 className="font-heading text-3xl lg:text-4xl font-bold mb-6 leading-tight">{move.action}</h2>
                  <div className="space-y-3 mb-8">
                    {move.why.map((w, i) => (
                      <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.1 }}
                        className="flex items-start gap-3 text-[color:var(--text-secondary)]">
                        <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--gold)] mt-2 shrink-0" />
                        <span>{w}</span>
                      </motion.div>
                    ))}
                  </div>
                  <div className="flex gap-3">
                    <button data-testid="focus-do-it" disabled={doing} onClick={doIt} className="gold-btn px-6 py-3 rounded-full inline-flex items-center gap-2 disabled:opacity-60">
                      {doing ? <><Loader2 className="w-4 h-4 animate-spin" /> Scheduling...</> : <>Do it <ArrowUpRight className="w-4 h-4" /></>}
                    </button>
                    <button onClick={() => setOpen(false)} className="px-6 py-3 rounded-full border border-[rgba(212,175,55,0.2)] hover:border-[color:var(--gold)] text-sm text-[color:var(--text-secondary)] transition">Later</button>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
